import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError } from 'rxjs';

import { environment } from '@env/environment';
import { Store } from '@ngrx/store';
import { ErrorService } from '../../store/error/error.service';
import { Logger } from '../logger';

const log = new Logger('ErrorHandlerInterceptor');

/**
 * Adds a default error handler to all requests.
 * Captures X-Ray trace ID and correlation ID from response headers for debugging.
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerInterceptor implements HttpInterceptor {
  private store = inject(Store);
  private errorService = inject(ErrorService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(catchError((error) => this.errorHandler(error, request)));
  }

  private errorHandler(response: HttpEvent<unknown>, request: HttpRequest<unknown>): Observable<HttpEvent<unknown>> {
    if (response instanceof HttpErrorResponse) {
      // Extract observability headers from error response
      const traceId = response.headers.get('X-Amzn-Trace-Id') ?? undefined;
      const correlationId = response.headers.get('X-Correlation-ID') ?? undefined;

      if (!environment.production) {
        log.error('Request error', {
          url: request.url,
          status: response.status,
          traceId,
          correlationId,
        });
      }

      // Send trace context to LogRocket for session-to-trace linking
      if (traceId || correlationId) {
        try {
          const lr = (window as unknown as Record<string, unknown>)['LogRocket'] as
            { track?: (event: string, properties: Record<string, string>) => void } | undefined;
          if (lr?.track) {
            lr.track('API Error', {
              url: request.url,
              status: String(response.status),
              ...(traceId ? { traceId } : {}),
              ...(correlationId ? { correlationId } : {}),
            });
          }
        } catch {
          // LogRocket not loaded — ignore
        }
      }
    }

    throw response;
  }
}
